import { Data, Effect, Schema, SchemaIssue } from "effect";
import { z } from "zod";

export const parserConfidenceKindSchema = z.enum([
  "vlm_self_reported",
  "ocr_char",
  "textlayer_exact",
  "heuristic",
  "unknown",
]);

export const parserPromptBboxOrderSchema = z.enum([
  "x1_y1_x2_y2",
  "y_min_x_min_y_max_x_max",
  "none",
  "vendor_native",
]);

export const parserPromptSchema = z.object({
  id: z.string().min(1),
  model: z.string().min(1),
  version: z.union([z.string(), z.number()]),
  technique: z.string().min(1),
  bbox_order: parserPromptBboxOrderSchema,
  system_prompt_ref: z.string().min(1).optional(),
  user_prompt_ref: z.string().min(1).optional(),
  system_prompt: z.string().optional(),
  user_prompt: z.string().optional(),
});

/**
 * Parser profiles make the old "engine" label explicit as model x prompt x
 * params. The confidence_kind tag addresses #408: OCR char confidence and VLM
 * self-reported confidence must not be compared as the same signal.
 */
export const parserProfileSchema = z.object({
  id: z.string().min(1),
  model: z.string().min(1),
  prompt_id: z.string().min(1),
  prompt_version: z.union([z.string(), z.number()]),
  params: z.record(z.string(), z.unknown()).optional(),
  confidence_kind: parserConfidenceKindSchema,
  cost_hints: z.object({
    per_page_usd: z.number().nonnegative().optional(),
    currency: z.string().min(1).optional(),
  }).optional(),
  // `false` = the profile is registered (contract/registry knowledge) but no
  // execution path honors its prompt yet. Selecting it must fail
  // not_implemented rather than run a different prompt and stamp this one —
  // stamped event properties must describe what executed (PR #431 review).
  executable: z.boolean().optional(),
});

export type ParserConfidenceKind = z.infer<typeof parserConfidenceKindSchema>;
export type ParserPrompt = z.infer<typeof parserPromptSchema>;
export type ParserProfile = z.infer<typeof parserProfileSchema>;

const NonEmptyString = Schema.String.check(Schema.isMinLength(1));
const PromptVersionCodec = Schema.Union([Schema.String, Schema.Number]);

/** Native parser-profile codecs; the Zod exports above remain for OpenAPI composition. */
export const ParserConfidenceKindCodec = Schema.Literals([
  "vlm_self_reported",
  "ocr_char",
  "textlayer_exact",
  "heuristic",
  "unknown",
]);

export const ParserPromptBboxOrderCodec = Schema.Literals([
  "x1_y1_x2_y2",
  "y_min_x_min_y_max_x_max",
  "none",
  "vendor_native",
]);

export const ParserPromptCodec = Schema.Struct({
  id: NonEmptyString,
  model: NonEmptyString,
  version: PromptVersionCodec,
  technique: NonEmptyString,
  bbox_order: ParserPromptBboxOrderCodec,
  system_prompt_ref: Schema.optionalKey(NonEmptyString),
  user_prompt_ref: Schema.optionalKey(NonEmptyString),
  system_prompt: Schema.optionalKey(Schema.String),
  user_prompt: Schema.optionalKey(Schema.String),
});

export const ParserProfileCodec = Schema.Struct({
  id: NonEmptyString,
  model: NonEmptyString,
  prompt_id: NonEmptyString,
  prompt_version: PromptVersionCodec,
  params: Schema.optionalKey(Schema.Record(Schema.String, Schema.Unknown)),
  confidence_kind: ParserConfidenceKindCodec,
  cost_hints: Schema.optionalKey(Schema.Struct({
    per_page_usd: Schema.optionalKey(Schema.Number.check(Schema.isGreaterThanOrEqualTo(0))),
    currency: Schema.optionalKey(NonEmptyString),
  })),
  executable: Schema.optionalKey(Schema.Boolean),
});

export type ParserProfileRegistryOperation = "decode_profile" | "decode_prompt";
export type ParserProfileRegistryIssue = {
  readonly message: string;
  readonly path: ReadonlyArray<PropertyKey>;
};

/** Redacted registry failure: issue paths are retained, profile payloads are not. */
export class ParserProfileRegistryError extends Data.TaggedError("ParserProfileRegistryError")<{
  readonly operation: ParserProfileRegistryOperation;
  readonly issues: ReadonlyArray<ParserProfileRegistryIssue>;
}> {}

export type ParserProfileResolutionOperation =
  | "resolve_prompt"
  | "resolve_profile_id"
  | "resolve_profile"
  | "resolve_model_prompt"
  | "resolve_executable_profile"
  | "resolve_executable_model_prompt";

/**
 * Redacted lookup failure. Caller-provided lookup strings are deliberately
 * omitted; non-executable failures retain only registered identifiers needed
 * by compatibility consumers to reproduce their existing error payloads.
 */
export class ParserProfileResolutionError extends Data.TaggedError("ParserProfileResolutionError")<{
  readonly operation: ParserProfileResolutionOperation;
  readonly reason: "not_found" | "not_executable";
  readonly message: string;
  readonly profileId?: string;
  readonly promptId?: string;
  readonly promptVersion?: string | number;
}> {}

type ParserProfileResolverError = ParserProfileRegistryError | ParserProfileResolutionError;

const formatStandardSchemaIssue = SchemaIssue.makeFormatterStandardSchemaV1();

function registryIssues(error: unknown): ReadonlyArray<ParserProfileRegistryIssue> {
  if (!Schema.isSchemaError(error)) {
    return [{ message: error instanceof Error ? error.message : String(error), path: [] }];
  }
  return formatStandardSchemaIssue(error.issue).issues.map((issue) => ({
    message: issue.message,
    path: issue.path?.map((segment) => typeof segment === "object" ? segment.key : segment) ?? [],
  }));
}

function decodeRegistryEffect<S extends Schema.ConstraintDecoder<unknown>>(
  operation: ParserProfileRegistryOperation,
  codec: S,
  input: unknown,
): Effect.Effect<S["Type"], ParserProfileRegistryError> {
  return Schema.decodeUnknownEffect(codec, { errors: "all" })(input).pipe(
    Effect.mapError((error) => new ParserProfileRegistryError({ operation, issues: registryIssues(error) })),
  );
}

export const decodeParserPromptEffect = (input: unknown) =>
  decodeRegistryEffect("decode_prompt", ParserPromptCodec, input).pipe(
    Effect.map((prompt) => prompt as ParserPrompt),
  );

export const decodeParserProfileEffect = (input: unknown) =>
  decodeRegistryEffect("decode_profile", ParserProfileCodec, input).pipe(
    Effect.map((profile) => profile as ParserProfile),
  );

export const GEMINI_3_FLASH_MODEL = "gemini-3-flash-preview";

export const BUILTIN_PARSER_PROMPTS = {
  "layout-divbbox@1": {
    id: "layout-divbbox",
    model: GEMINI_3_FLASH_MODEL,
    version: 1,
    technique: "div_wrapped_markdown_bbox",
    bbox_order: "x1_y1_x2_y2",
    system_prompt_ref: "SYSTEM_PROMPT_LAYOUT",
    user_prompt_ref: "USER_PROMPT_LAYOUT",
  },
  "layout-bbox-parsebench@1": {
    id: "layout-bbox-parsebench",
    model: GEMINI_3_FLASH_MODEL,
    version: 1,
    technique: "parsebench_gemini_native_bbox",
    bbox_order: "y_min_x_min_y_max_x_max",
    system_prompt_ref: "SYSTEM_PROMPT_LAYOUT_GEMINI",
    user_prompt_ref: "USER_PROMPT_LAYOUT_GEMINI",
  },
  // The prompt the gemini worker ACTUALLY executes today
  // (gemini-vision-parser-agent/src/gemini.ts hardcodes the multipage GEMINI
  // pair). The default profile must point here so stamps match execution.
  "layout-bbox-gemini-multipage@1": {
    id: "layout-bbox-gemini-multipage",
    model: GEMINI_3_FLASH_MODEL,
    version: 1,
    technique: "parsebench_gemini_native_bbox_multipage",
    bbox_order: "y_min_x_min_y_max_x_max",
    system_prompt_ref: "SYSTEM_PROMPT_LAYOUT_GEMINI_MULTIPAGE",
    user_prompt_ref: "USER_PROMPT_LAYOUT_GEMINI_MULTIPAGE",
  },
} as const satisfies Record<string, ParserPrompt>;

export const BUILTIN_PARSER_PROFILES = {
  "gemini-3-flash-minimal": {
    id: "gemini-3-flash-minimal",
    model: GEMINI_3_FLASH_MODEL,
    // Matches the prompt the worker executes (multipage GEMINI pair) — see
    // BUILTIN_PARSER_PROMPTS["layout-bbox-gemini-multipage@1"].
    prompt_id: "layout-bbox-gemini-multipage",
    prompt_version: 1,
    params: {
      vendor: "gemini-vision",
      variant: "gemini-3-flash-minimal",
    },
    confidence_kind: "vlm_self_reported",
    cost_hints: { per_page_usd: 0.003, currency: "USD" },
  },
  "gemini-3-flash-parsebench": {
    id: "gemini-3-flash-parsebench",
    model: GEMINI_3_FLASH_MODEL,
    prompt_id: "layout-bbox-parsebench",
    prompt_version: 1,
    params: {
      vendor: "gemini-vision",
      variant: "gemini-3-flash-minimal",
    },
    confidence_kind: "vlm_self_reported",
    cost_hints: { per_page_usd: 0.003, currency: "USD" },
    // The worker does not yet accept a prompt selector; selecting this profile
    // must fail not_implemented rather than execute the default prompt and
    // stamp this one.
    executable: false,
  },
  textlayer: {
    id: "textlayer",
    model: "pdf-textlayer",
    prompt_id: "textlayer-native",
    prompt_version: 1,
    params: { vendor: "textlayer" },
    confidence_kind: "textlayer_exact",
    cost_hints: { per_page_usd: 0, currency: "USD" },
  },
  llamaparse: {
    id: "llamaparse",
    model: "llamaparse",
    prompt_id: "llamaparse-default",
    prompt_version: 1,
    params: { vendor: "llamaparse" },
    confidence_kind: "unknown",
  },
  mineru: {
    id: "mineru",
    model: "mineru",
    prompt_id: "mineru-default",
    prompt_version: 1,
    params: { vendor: "mineru" },
    confidence_kind: "heuristic",
  },
  reducto: {
    id: "reducto",
    model: "reducto",
    prompt_id: "reducto-default",
    prompt_version: 1,
    params: { vendor: "reducto" },
    confidence_kind: "unknown",
  },
  docling: {
    id: "docling",
    model: "docling",
    prompt_id: "docling-default",
    prompt_version: 1,
    params: { vendor: "docling" },
    confidence_kind: "heuristic",
  },
  unstructured: {
    id: "unstructured",
    model: "unstructured",
    prompt_id: "unstructured-default",
    prompt_version: 1,
    params: { vendor: "unstructured" },
    confidence_kind: "unknown",
  },
  google_document_ai: {
    id: "google_document_ai",
    model: "google-document-ai",
    prompt_id: "google-document-ai-default",
    prompt_version: 1,
    params: { vendor: "docai-gcs" },
    confidence_kind: "ocr_char",
  },
  azure_document_intelligence: {
    id: "azure_document_intelligence",
    model: "azure-document-intelligence",
    prompt_id: "azure-document-intelligence-default",
    prompt_version: 1,
    params: { vendor: "azure-di" },
    confidence_kind: "ocr_char",
  },
  aws_textract: {
    id: "aws_textract",
    model: "aws-textract",
    prompt_id: "aws-textract-default",
    prompt_version: 1,
    params: { vendor: "aws_textract" },
    confidence_kind: "ocr_char",
  },
  mistral_ocr: {
    id: "mistral_ocr",
    model: "mistral-ocr",
    prompt_id: "mistral-ocr-default",
    prompt_version: 1,
    params: { vendor: "mistral_ocr" },
    confidence_kind: "ocr_char",
  },
  custom: {
    id: "custom",
    model: "custom",
    prompt_id: "custom",
    prompt_version: 1,
    params: { vendor: "custom" },
    confidence_kind: "unknown",
  },
} as const satisfies Record<string, ParserProfile>;

export const PARSER_PROFILE_ALIASES = {
  auto: "gemini-3-flash-minimal",
  okrapdf: "gemini-3-flash-minimal",
  gemini: "gemini-3-flash-minimal",
  "gemini-vision": "gemini-3-flash-minimal",
  "gemini-3-flash": "gemini-3-flash-minimal",
  "gemini-3-flash-preview": "gemini-3-flash-minimal",
  "gemini-3-flash-minimal": "gemini-3-flash-minimal",
  "layout-divbbox": "gemini-3-flash-minimal",
  "layout-divbbox@1": "gemini-3-flash-minimal",
  "layout-bbox-parsebench": "gemini-3-flash-parsebench",
  "layout-bbox-parsebench@1": "gemini-3-flash-parsebench",
  "gemini-3-flash-parsebench": "gemini-3-flash-parsebench",
  textlayer: "textlayer",
  llamaparse: "llamaparse",
  llama_parse: "llamaparse",
  mineru: "mineru",
  reducto: "reducto",
  docling: "docling",
  unstructured: "unstructured",
  unstructuredio: "unstructured",
  google_document_ai: "google_document_ai",
  "google-document-ai": "google_document_ai",
  docai: "google_document_ai",
  "docai-gcs": "google_document_ai",
  azure_document_intelligence: "azure_document_intelligence",
  "azure-document-intelligence": "azure_document_intelligence",
  "azure-di": "azure_document_intelligence",
  aws_textract: "aws_textract",
  "aws-textract": "aws_textract",
  mistral_ocr: "mistral_ocr",
  "mistral-ocr": "mistral_ocr",
  custom: "custom",
  "parse-proxy": "custom",
} as const satisfies Record<string, keyof typeof BUILTIN_PARSER_PROFILES>;

function normalizeId(value: string): string {
  return value.trim().toLowerCase();
}

function notFound(
  operation: ParserProfileResolutionOperation,
): ParserProfileResolutionError {
  return new ParserProfileResolutionError({
    operation,
    reason: "not_found",
    message: "No registered parser profile entry matched the request",
  });
}

function notExecutable(
  operation: ParserProfileResolutionOperation,
  profile: ParserProfile,
): ParserProfileResolutionError {
  return new ParserProfileResolutionError({
    operation,
    reason: "not_executable",
    message: "The registered parser profile is not executable",
    profileId: profile.id,
    promptId: profile.prompt_id,
    promptVersion: profile.prompt_version,
  });
}

function reScopeResolution(
  error: ParserProfileResolutionError,
  operation: ParserProfileResolutionOperation,
): ParserProfileResolutionError {
  return new ParserProfileResolutionError({
    operation,
    reason: error.reason,
    message: error.message,
    profileId: error.profileId,
    promptId: error.promptId,
    promptVersion: error.promptVersion,
  });
}

/** Natural model spellings → canonical model ids (PR #431 review finding 2:
 * `--model gemini-3-flash` must resolve, not error). */
export const PARSER_MODEL_ALIASES = {
  "gemini-3-flash": GEMINI_3_FLASH_MODEL,
  "gemini-flash": GEMINI_3_FLASH_MODEL,
  gemini: GEMINI_3_FLASH_MODEL,
  "gemini-vision": GEMINI_3_FLASH_MODEL,
  textlayer: "pdf-textlayer",
  "pdf-textlayer": "pdf-textlayer",
} as const satisfies Record<string, string>;

function resolveParserModelValue(model: string): string {
  const normalized = normalizeId(model);
  return PARSER_MODEL_ALIASES[normalized as keyof typeof PARSER_MODEL_ALIASES] ?? normalized;
}

export const resolveParserModelEffect = Effect.fn("okrapdf/schemas.resolveParserModel")(
  function* (model: string) {
    return resolveParserModelValue(model);
  },
);

export function resolveParserModel(model: string): string {
  return Effect.runSync(resolveParserModelEffect(model));
}

export function isParserProfileExecutable(profile: ParserProfile | null | undefined): boolean {
  return !!profile && profile.executable !== false;
}

const decodeBuiltinParserProfilesEffect = Effect.fn(
  "okrapdf/schemas.decodeBuiltinParserProfiles",
)(function* () {
  return yield* Effect.forEach(
    Object.values(BUILTIN_PARSER_PROFILES),
    (profile) => decodeParserProfileEffect(profile),
  );
});

export const listExecutableParserProfilesEffect = Effect.fn(
  "okrapdf/schemas.listExecutableParserProfiles",
)(function* () {
  const profiles = yield* decodeBuiltinParserProfilesEffect();
  return profiles.filter((profile) => isParserProfileExecutable(profile));
});

function runRegistrySync<A>(effect: Effect.Effect<A, ParserProfileRegistryError>): A {
  return Effect.runSync(effect.pipe(Effect.catch((error) => Effect.die(error))));
}

function runOptionalResolver<A>(
  effect: Effect.Effect<A, ParserProfileResolverError>,
): A | undefined {
  return Effect.runSync(
    effect.pipe(
      Effect.catch((error) =>
        error._tag === "ParserProfileResolutionError" && error.reason === "not_found"
          ? Effect.succeed(undefined)
          : Effect.die(error),
      ),
    ),
  );
}

export function listExecutableParserProfiles(): ParserProfile[] {
  return runRegistrySync(listExecutableParserProfilesEffect());
}

export function promptRegistryKey(promptId: string, version: string | number): string {
  return `${promptId}@${String(version)}`;
}

export const resolveParserPromptEffect = Effect.fn(
  "okrapdf/schemas.resolveParserPrompt",
)(function* (
  promptIdOrKey: string,
  version?: string | number,
) {
  const key = version === undefined
    ? normalizeId(promptIdOrKey)
    : promptRegistryKey(normalizeId(promptIdOrKey), version);
  const byKey = BUILTIN_PARSER_PROMPTS[key as keyof typeof BUILTIN_PARSER_PROMPTS];
  if (byKey) return yield* decodeParserPromptEffect(byKey);

  const prompt = Object.values(BUILTIN_PARSER_PROMPTS).find((candidate) =>
    normalizeId(candidate.id) === key ||
    promptRegistryKey(normalizeId(candidate.id), candidate.version) === key
  );
  return prompt
    ? yield* decodeParserPromptEffect(prompt)
    : yield* Effect.fail(notFound("resolve_prompt"));
});

export function resolveParserPrompt(
  promptIdOrKey: string,
  version?: string | number,
): ParserPrompt | undefined {
  return runOptionalResolver(resolveParserPromptEffect(promptIdOrKey, version));
}

export const resolveParserProfileIdEffect = Effect.fn(
  "okrapdf/schemas.resolveParserProfileId",
)(function* (idOrAlias: string) {
  const normalized = normalizeId(idOrAlias);
  if (normalized in BUILTIN_PARSER_PROFILES) {
    const profile = yield* decodeParserProfileEffect(
      BUILTIN_PARSER_PROFILES[normalized as keyof typeof BUILTIN_PARSER_PROFILES],
    );
    return profile.id;
  }

  const profileId = PARSER_PROFILE_ALIASES[normalized as keyof typeof PARSER_PROFILE_ALIASES];
  if (!profileId) return yield* Effect.fail(notFound("resolve_profile_id"));

  const profile = yield* decodeParserProfileEffect(BUILTIN_PARSER_PROFILES[profileId]);
  return profile.id;
});

export function resolveParserProfileId(idOrAlias: string): string | undefined {
  return runOptionalResolver(resolveParserProfileIdEffect(idOrAlias));
}

export const resolveParserProfileEffect = Effect.fn(
  "okrapdf/schemas.resolveParserProfile",
)(function* (idOrAlias: string) {
  const id = yield* resolveParserProfileIdEffect(idOrAlias).pipe(
    Effect.mapError((error) =>
      error._tag === "ParserProfileResolutionError"
        ? reScopeResolution(error, "resolve_profile")
        : error,
    ),
  );
  const profile = BUILTIN_PARSER_PROFILES[id as keyof typeof BUILTIN_PARSER_PROFILES];
  return profile
    ? yield* decodeParserProfileEffect(profile)
    : yield* Effect.fail(notFound("resolve_profile"));
});

export function resolveParserProfile(idOrAlias: string): ParserProfile | undefined {
  return runOptionalResolver(resolveParserProfileEffect(idOrAlias));
}

export const resolveParserProfileByModelPromptEffect = Effect.fn(
  "okrapdf/schemas.resolveParserProfileByModelPrompt",
)(function* (
  model: string,
  promptIdOrKey: string,
  promptVersion?: string | number,
) {
  const normalizedModel = yield* resolveParserModelEffect(model);
  const bare = normalizeId(promptIdOrKey);
  // Accept both "prompt-id" and "prompt-id@version" spellings.
  const atIndex = bare.lastIndexOf("@");
  const normalizedPrompt = atIndex > 0 ? bare.slice(0, atIndex) : bare;
  const versionFromKey = atIndex > 0 ? bare.slice(atIndex + 1) : undefined;
  const wantedVersion = promptVersion !== undefined ? String(promptVersion) : versionFromKey;
  const profiles = yield* decodeBuiltinParserProfilesEffect();
  const matches = profiles.filter((profile) =>
    resolveParserModelValue(profile.model) === normalizedModel &&
    normalizeId(profile.prompt_id) === normalizedPrompt &&
    (wantedVersion === undefined || String(profile.prompt_version) === wantedVersion)
  );
  // Prefer an executable profile when both an executable and a declared-only
  // profile share the same (model, prompt) pair.
  const profile = matches.find((candidate) => isParserProfileExecutable(candidate)) ?? matches[0];
  return profile ?? (yield* Effect.fail(notFound("resolve_model_prompt")));
});

export function resolveParserProfileByModelPrompt(
  model: string,
  promptIdOrKey: string,
  promptVersion?: string | number,
): ParserProfile | undefined {
  return runOptionalResolver(
    resolveParserProfileByModelPromptEffect(model, promptIdOrKey, promptVersion),
  );
}

export const resolveExecutableParserProfileEffect = Effect.fn(
  "okrapdf/schemas.resolveExecutableParserProfile",
)(function* (idOrAlias: string) {
  const profile = yield* resolveParserProfileEffect(idOrAlias).pipe(
    Effect.mapError((error) =>
      error._tag === "ParserProfileResolutionError"
        ? reScopeResolution(error, "resolve_executable_profile")
        : error,
    ),
  );
  return isParserProfileExecutable(profile)
    ? profile
    : yield* Effect.fail(notExecutable("resolve_executable_profile", profile));
});

export const resolveExecutableParserProfileByModelPromptEffect = Effect.fn(
  "okrapdf/schemas.resolveExecutableParserProfileByModelPrompt",
)(function* (
  model: string,
  promptIdOrKey: string,
  promptVersion?: string | number,
) {
  const profile = yield* resolveParserProfileByModelPromptEffect(
    model,
    promptIdOrKey,
    promptVersion,
  ).pipe(
    Effect.mapError((error) =>
      error._tag === "ParserProfileResolutionError"
        ? reScopeResolution(error, "resolve_executable_model_prompt")
        : error,
    ),
  );
  return isParserProfileExecutable(profile)
    ? profile
    : yield* Effect.fail(notExecutable("resolve_executable_model_prompt", profile));
});
